// @flow

import React, { PureComponent } from 'react';
import qs from 'qs';
import debounce from 'lodash.debounce';
import { Map } from 'immutable';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n, Analytics } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import AddUpdateItemModal from 'main-app/components/AddUpdateItemModal';
import ExportCSVModal from 'main-app/components/ExportCSVModal';
import FilterControls, {
  type FilterValue,
} from 'main-app/components/FilterControls';
import type { RouterHistory } from 'react-router';
import getSortOptions from './getSortOptions';
import getFilterOptions from './getFilterOptions';
import PaginatedItemsContainer from './PaginatedItemsContainer';

type Props = {
  history: RouterHistory,
};

type State = {
  addModalOpen: boolean,
  exportModalOpen: boolean,
  createAnotherItem: boolean,
  search: string,
  graphqlSearchValue: string,
};

const DEFAULT_FILTER = {
  sort: 'CREATED_AT',
  direction: 'DESC',
};

class Items extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      addModalOpen: false,
      exportModalOpen: false,
      createAnotherItem: false,
      search: '',
      graphqlSearchValue: '',
    };

    this.handleDebouncedSearch = debounce(this.handleDebouncedSearch, 500);
  }

  getFilters = (): FilterValue => {
    const { history } = this.props;
    const { search } = history.location;

    if (!search) {
      return DEFAULT_FILTER;
    }

    return {
      ...DEFAULT_FILTER,
      ...qs.parse(search, { ignoreQueryPrefix: true }),
    };
  };

  setFilters = (filters: Object) => {
    const { history } = this.props;

    history.replace(
      '/items?' +
        qs.stringify(filters, {
          encode: true,
          arrayFormat: 'brackets',
        }),
    );
  };

  filtersDirty = () => {
    const { graphqlSearchValue } = this.state;

    return (
      !Map(this.getFilters()).equals(Map(DEFAULT_FILTER)) || graphqlSearchValue
    );
  };

  compileQueryVariables = () => {
    const filters = this.getFilters();
    const { graphqlSearchValue } = this.state;

    const queryVariables = {
      ...filters,
      search: graphqlSearchValue.trim() || null,
      sortBy: {
        field: filters.sort,
        direction: filters.direction,
      },
    };

    if (queryVariables.orderedAt) {
      const [start, end] = queryVariables.orderedAt.split(' - ');
      queryVariables.orderedAtStart = start;
      queryVariables.orderedAtEnd = end;

      delete queryVariables.orderedAt;
    }

    if (queryVariables.requiredAt) {
      const [start, end] = queryVariables.requiredAt.split(' - ');
      queryVariables.requiredAtStart = start;
      queryVariables.requiredAtEnd = end;

      delete queryVariables.requiredAt;
    }

    if (queryVariables.promisedAt) {
      const [start, end] = queryVariables.promisedAt.split(' - ');
      queryVariables.promisedAtStart = start;
      queryVariables.promisedAtEnd = end;

      delete queryVariables.promisedAt;
    }

    delete queryVariables.sort;
    delete queryVariables.direction;

    return queryVariables;
  };

  handleSearchInput = (search: string) => {
    this.setState(
      {
        search,
      },
      this.handleDebouncedSearch,
    );
  };

  handleDebouncedSearch = () => {
    const { search } = this.state;

    this.setState({
      graphqlSearchValue: search,
    });

    Analytics.trackEvent('Search Applied', {
      listName: 'Inventory, Item',
      searchString: search,
    });
  };

  handleCSVExport = async (columns: string[]) => {
    const gqlQueryArgs = {
      ...this.compileQueryVariables(),
    };

    window.open(
      `/api/v1/csv-export-items?` +
        qs.stringify(
          {
            gqlQueryArgs,
            columns,
          },
          {
            encode: true,
            arrayFormat: 'brackets',
          },
        ),
    );
  };

  handleOpenCreateModal = () => {
    this.setState({
      addModalOpen: true,
    });
  };

  handleOpenExportModal = () => {
    this.setState({
      exportModalOpen: true,
    });
  };

  handleCloseAllModals = () => {
    this.setState({
      addModalOpen: false,
      exportModalOpen: false,
      createAnotherItem: false,
    });
  };

  handleAddModalReopen = () => {
    this.setState(
      {
        addModalOpen: false,
        exportModalOpen: false,
        createAnotherItem: true,
      },
      () => {
        this.setState({
          addModalOpen: true,
        });
      },
    );
  };

  render() {
    const { history } = this.props;
    const {
      search,
      addModalOpen,
      exportModalOpen,
      createAnotherItem,
    } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{
          force: true,
        }}
        query={graphql`
          query ItemsQuery(
            $count: Int!
            $search: String
            $cursor: String
            $sortBy: ItemSort
            $partNumber: String
            $itemNumbers: [String!]
            $orderIds: [ID!]
            $customerIds: [ID!]
            $itemCategoryIds: [ID!]
          ) {
            ...PaginatedItemsContainer_items
              @arguments(
                search: $search
                sortBy: $sortBy
                partNumber: $partNumber
                itemNumbers: $itemNumbers
                orderIds: $orderIds
                customerIds: $customerIds
                itemCategoryIds: $itemCategoryIds
              )

            viewer {
              id
              permissions
            }

            forms(types: [ITEM]) {
              edges {
                node {
                  id
                  type
                  ...AddUpdateItemModal_defaultForm
                  ...ItemsTable_defaultForm
                }
              }
            }
          }
        `}
        variables={{
          ...this.compileQueryVariables(),
          count: 30,
          cursor: null,
        }}
        render={({ props }) => (
          <Page>
            <PageHeaderList
              title={i18n.t('Inventory')}
              exportButtonLabel={props && i18n.t('Export to CSV')}
              onExportButtonClick={this.handleOpenExportModal}
              createButtonLabel={
                props &&
                props.viewer.permissions.includes('CREATE_ITEM') &&
                i18n.t('New Item')
              }
              onCreateButtonClick={this.handleOpenCreateModal}
              searchPlaceholder={i18n.t('Inventory #, Part #, Item Name')}
              searchValue={search}
              onSearchChange={this.handleSearchInput}
            />
            <FilterControls
              value={this.getFilters()}
              defaultValue={DEFAULT_FILTER}
              sortOptions={getSortOptions()}
              filterOptions={getFilterOptions()}
              savedFilterType="ITEM"
              onFiltersChange={filters => this.setFilters(filters)}
            />
            {!props ? (
              <Loader />
            ) : (
              <PaginatedItemsContainer
                history={history}
                filtersDirty={this.filtersDirty()}
                onCreateNew={this.handleOpenCreateModal}
                items={props}
                deleteEnabled
                editEnabled={props.viewer.permissions.includes('UPDATE_ITEM')}
                defaultForm={props.forms.edges[0]?.node || null}
              />
            )}
            {addModalOpen && (
              <AddUpdateItemModal
                onClose={this.handleCloseAllModals}
                onSuccess={item => history.push(`/item/${item.id}`)}
                onReopen={this.handleAddModalReopen}
                createAnotherItem={createAnotherItem}
                item={null}
                defaultForm={props.forms.edges[0]?.node || null}
              />
            )}
            {exportModalOpen && (
              <ExportCSVModal
                onClose={this.handleCloseAllModals}
                title={i18n.t('Export Items to CSV')}
                columnsApiUrl="/api/v1/csv-export-items/columns"
                onSubmit={this.handleCSVExport}
              />
            )}
          </Page>
        )}
      />
    );
  }
}

export default Items;
